// ============================================================
// 烟花：点击「发送信号」后全屏绽放 20 秒（Canvas 粒子系统）
// - 全屏覆盖层，置于页面最前（z-index 最高）
// - pointer-events: none，不遮挡任何交互
// - 20s 内持续发射火箭，结束后粒子自然消散并自动移除画布
// ============================================================

import { showToast } from "./ui";

const FIREWORK_MS = 20000; // 烟花持续时长
const COLORS = ["#00ff9c", "#00e5ff", "#ff2e88", "#ffb000", "#e8f4ff"];
const ROCKET_GAP = [360, 720]; // 两枚火箭的随机发射间隔（ms）

let canvas = null;
let ctx = null;
let raf = 0;
let running = false;
let startedAt = 0;
let nextRocketAt = 0;
let rockets = [];
let sparks = [];
let onResize = null;
let stopTimer = null;

const rand = (a, b) => a + Math.random() * (b - a);
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

function ensureCanvas() {
  if (canvas) return;
  canvas = document.createElement("canvas");
  canvas.className = "fireworks-canvas";
  canvas.setAttribute("aria-hidden", "true");
  document.body.appendChild(canvas);
  ctx = canvas.getContext("2d");
  resize();
  onResize = resize;
  window.addEventListener("resize", onResize);
}

function resize() {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = Math.floor(window.innerWidth * dpr);
  canvas.height = Math.floor(window.innerHeight * dpr);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}

function launchRocket() {
  const w = window.innerWidth;
  const h = window.innerHeight;
  rockets.push({
    x: rand(w * 0.12, w * 0.88),
    y: h + 10,
    vy: -rand(8.5, 12.5), // 上升初速（向上为负）
    color: pick(COLORS),
    targetY: rand(h * 0.15, h * 0.45),
    trail: [],
  });
}

function explode(r) {
  const n = 56 + Math.floor(Math.random() * 40);
  for (let i = 0; i < n; i++) {
    const angle = (Math.PI * 2 * i) / n + rand(-0.18, 0.18);
    const speed = rand(1.5, 5.6);
    sparks.push({
      x: r.x,
      y: r.y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life: 1,
      decay: rand(0.011, 0.02),
      color: r.color,
      size: rand(1.2, 2.6),
    });
  }
}

function drawSparks(w, h) {
  for (let i = sparks.length - 1; i >= 0; i--) {
    const s = sparks[i];
    s.x += s.vx;
    s.y += s.vy;
    s.vy += 0.042; // 重力
    s.vx *= 0.985; // 空气阻力
    s.vy *= 0.985;
    s.life -= s.decay;

    if (s.life <= 0 || s.y > h + 24 || s.x < -24 || s.x > w + 24) {
      sparks.splice(i, 1);
      continue;
    }
    ctx.globalAlpha = Math.max(s.life, 0);
    ctx.fillStyle = s.color;
    ctx.beginPath();
    ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawRockets() {
  for (let i = rockets.length - 1; i >= 0; i--) {
    const r = rockets[i];
    r.trail.push({ x: r.x, y: r.y, life: 0.55 });
    r.y += r.vy;
    r.vy += 0.055; // 上升减速

    // 到达目标高度或速度耗尽 → 爆炸
    if (r.y <= r.targetY || r.vy >= -0.9) {
      explode(r);
      rockets.splice(i, 1);
      continue;
    }

    // 拖尾
    for (let j = r.trail.length - 1; j >= 0; j--) {
      const t = r.trail[j];
      ctx.globalAlpha = Math.max(t.life, 0);
      ctx.fillStyle = r.color;
      ctx.fillRect(t.x, t.y, 2, 2);
      t.life -= 0.045;
      if (t.life <= 0) r.trail.splice(j, 1);
    }
    // 火箭头
    ctx.globalAlpha = 1;
    ctx.fillStyle = "#e8f4ff";
    ctx.fillRect(r.x - 1.5, r.y - 3, 3, 7);
  }
  ctx.globalAlpha = 1;
}

function tick() {
  raf = requestAnimationFrame(tick);
  const now = performance.now();
  const elapsed = now - startedAt;
  const w = window.innerWidth;
  const h = window.innerHeight;

  ctx.clearRect(0, 0, w, h);

  // 20s 内持续发射
  if (elapsed < FIREWORK_MS && now >= nextRocketAt) {
    launchRocket();
    nextRocketAt = now + rand(ROCKET_GAP[0], ROCKET_GAP[1]);
  }

  drawSparks(w, h);
  drawRockets();

  // 结束后等粒子自然消散，再移除画布
  if (elapsed >= FIREWORK_MS && rockets.length === 0 && sparks.length === 0) {
    stop();
  }
}

function launchFireworks() {
  ensureCanvas();
  rockets = [];
  sparks = [];
  startedAt = performance.now();
  nextRocketAt = startedAt + 240;
  // 兜底：无论 rAF 是否受限，20s 后必然停止并移除画布
  clearTimeout(stopTimer);
  stopTimer = setTimeout(stop, FIREWORK_MS + 2500);
  if (!running) {
    running = true;
    raf = requestAnimationFrame(tick);
  }
  showToast("📡 信号已发送 —— 全城烟花已就绪", 3000);
}

function stop() {
  running = false;
  cancelAnimationFrame(raf);
  clearTimeout(stopTimer);
  stopTimer = null;
  if (onResize) window.removeEventListener("resize", onResize);
  onResize = null;
  if (canvas) {
    canvas.remove();
    canvas = null;
    ctx = null;
  }
}

export default function initFireworks() {
  const btn = document.getElementById("contact-signal");
  if (!btn) return;
  btn.addEventListener("click", (e) => {
    e.preventDefault();
    launchFireworks();
  });
}
